/**
 * @bos/portal3 — one app (route `/app/apps/:id`, inside <AppShell>).
 *
 * An app is a thing in its own right, so it gets what a thing gets: its own addressable
 * URL, where Back / Forward / Refresh / deep-link all work because the id comes from the
 * path and the page fetches on mount. The server resolves the id owner-scoped, so an app
 * that is missing OR belongs to someone else arrives as a clean `not-found` state here —
 * never another user's data (IDOR-safe by construction).
 *
 * The page is deliberately short: what the app is called, which business it belongs to,
 * the name its download will carry, the door to build it (again), and every build it has
 * been through. Documents are NOT here — they describe the BUSINESS, so they live on the
 * project page and every app of that business reads the same ones.
 */
import { Link, useParams } from 'react-router-dom';
import { PATHS } from '../routes';
import { PageHeader } from '../components/PageHeader';
import { useApp } from '../apps/hooks';
import { StartRunSection } from '../runs/StartRunSection';

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

export function AppPage() {
  const { id = '' } = useParams<{ id: string }>();
  const { app, projectName, status, error, reload } = useApp(id);

  if (status === 'loading') {
    return (
      <section aria-busy="true">
        <p className="page__note">Loading app…</p>
      </section>
    );
  }

  if (status === 'not-found') {
    return (
      <section>
        <PageHeader
          eyebrow="Not found"
          title="App not found"
          lede="This app doesn’t exist, or it isn’t yours. It may have been removed."
        />
        <Link className="btn btn--primary" to={PATHS.dashboard}>
          Back to your projects
        </Link>
      </section>
    );
  }

  if (status === 'error' || app === null) {
    return (
      <section>
        <PageHeader eyebrow="App" title="Something went wrong" />
        <div className="projects__state">
          <p className="form__error" role="alert">
            {error ?? 'We couldn’t load this app.'}
          </p>
          <div className="form__actions">
            <button className="btn btn--ghost" type="button" onClick={() => void reload()}>
              Try again
            </button>
            <Link className="btn btn--quiet" to={PATHS.dashboard}>
              Back to your projects
            </Link>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="page--app">
      <nav className="crumbs" aria-label="Breadcrumb">
        <Link to={PATHS.dashboard}>Your projects</Link>
        <span className="crumbs__sep" aria-hidden="true">
          /
        </span>
        <Link to={PATHS.project(app.projectId)}>{projectName ?? 'Project'}</Link>
        <span className="crumbs__sep" aria-hidden="true">
          /
        </span>
        <span className="crumbs__here">{app.name}</span>
      </nav>

      <PageHeader eyebrow="App" title={app.name} />
      <div className="project-meta">
        <span>
          An app of {projectName ?? 'this business'} · named {formatDate(app.createdAt)}
        </span>
        {/* The slug is not decoration: it is the name the finished app is built and
            downloaded under, and the reason two apps here can never be confused. */}
        <span>downloads as {app.slug}.zip</span>
      </div>

      <div className="stack" style={{ marginTop: '1.75rem' }}>
        <StartRunSection appId={app.id} projectId={app.projectId} />
      </div>
    </section>
  );
}
